import { FC, useEffect, useMemo } from 'react';
import { css } from '@emotion/react';
import composeRefs from '@seznam/compose-react-refs';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { i18next, useTranslation } from '@frontend/i18n';
import { useAppFlagStore, useVoiceAIRCustomizationFlag } from '@frontend/shared';
import { Button, PopoverDialog, Text, usePopoverDialog } from '@frontend/design-system';
import { CalloutBanner } from '../../../common/components/promo-banners';
import {
  CALL_INSIGHT_FEEDBACK_CALLOUT_BANNER_BETA_VARIANT_FF,
  CALL_INSIGHT_FEEDBACK_CALLOUT_BANNER_FF,
} from '../../constants';
import { useCallIntelInAppBannerVisibility } from '../../hooks/use-call-intel-in-app-banner-visibility';
import { getInAppAwarenessBannerTrackingId } from '../../tracking-ids';
import { InAppAwarenessPromoSource } from '../../types';

const CALL_INTEL_CF = 'callintelligence';
const ACTIVE_STATES = [State.ACTIVE, State.PROMOTE];

const COMPONENT_ID: InAppAwarenessPromoSource = 'call-insight-feedback-callout';

type FeedbackCalloutVariant = 'ci-air' | 'ci-only' | 'air-only' | 'no-ci-no-air';

interface CallInsightFeedbackCalloutProps {
  locationId?: string;
  isAIRCall?: boolean;
  isDemo?: boolean;
  anchorRef: React.RefObject<HTMLElement>;
  innerRef?: React.RefObject<HTMLDivElement>;
  onDismiss?: () => void;
}

const getVariantContent = (variant: FeedbackCalloutVariant) => {
  switch (variant) {
    case 'ci-air':
      return {
        title: i18next.t('Share feedback about this call', { ns: 'call-intelligence' }),
        description: i18next.t(
          'You can now use the thumbs up 👍 or thumbs down 👎 to share feedback across all Call Insights, including Call Intelligence and AI Receptionist calls.',
          { ns: 'call-intelligence' }
        ),
      };
    case 'ci-only':
      return {
        title: i18next.t('The feedback buttons have moved', { ns: 'call-intelligence' }),
        description: i18next.t(
          'Along with sharing feedback on Call Intelligence, you can now use the thumbs up 👍 or thumbs down 👎 to share feedback on all information in your Call Insights.',
          { ns: 'call-intelligence' }
        ),
      };
    case 'air-only':
      return {
        title: i18next.t('Help us improve the AI Receptionist', { ns: 'call-intelligence' }),
        description: i18next.t(
          'Leave feedback here when something looks right or off. Your input helps Weave improve the AI Receptionist and Call Insights.',
          { ns: 'call-intelligence' }
        ),
      };
    case 'no-ci-no-air':
    default:
      return {
        title: i18next.t('Help us improve your Call Insights', { ns: 'call-intelligence' }),
        description: i18next.t(
          'Give your Call Insights a thumbs up 👍 or thumbs down 👎. We review your feedback to make insights more accurate and useful.',
          { ns: 'call-intelligence' }
        ),
      };
  }
};

export const CallInsightFeedbackCallout: FC<CallInsightFeedbackCalloutProps> = ({
  locationId,
  isAIRCall = false,
  isDemo,
  anchorRef,
  innerRef,
  onDismiss,
}) => {
  const { t } = useTranslation('call-intelligence');
  const selectedLocationIds = useMemo(() => (locationId ? [locationId] : []), [locationId]);
  const { getCustomizationFlagLocationMap } = useAppFlagStore();

  const { locationFlagMap: voiceAIRLocationFlagMap } = useVoiceAIRCustomizationFlag(selectedLocationIds);

  const {
    getDialogProps,
    open,
    close,
    refs: popoverRefs,
  } = usePopoverDialog({
    placement: 'top',
  });

  // Compute CI and AIR subscription status based on customization flags
  const { hasCISubscription, hasAIRSubscription } = useMemo(() => {
    if (!selectedLocationIds.length) return { hasCISubscription: false, hasAIRSubscription: false };

    const callIntelLocationFlagMap = getCustomizationFlagLocationMap({
      locationIds: selectedLocationIds,
      flagName: CALL_INTEL_CF,
    });

    return {
      hasCISubscription: selectedLocationIds.some((location) => {
        const state = callIntelLocationFlagMap[location]?.state;
        return !!state && ACTIVE_STATES.includes(state);
      }),
      hasAIRSubscription: selectedLocationIds.some((location) => {
        const state = voiceAIRLocationFlagMap[location]?.state;
        return !!state && ACTIVE_STATES.includes(state);
      }),
    };
  }, [selectedLocationIds, getCustomizationFlagLocationMap, voiceAIRLocationFlagMap]);

  // Determine variant based on subscriptions
  const variant: FeedbackCalloutVariant = useMemo(() => {
    if (hasCISubscription && hasAIRSubscription) return 'ci-air';
    if (hasCISubscription && !hasAIRSubscription) return 'ci-only';
    if (!hasCISubscription && hasAIRSubscription) return 'air-only';
    return 'no-ci-no-air';
  }, [hasCISubscription, hasAIRSubscription]);

  // For AIR-only customers, only show on AIR calls
  // For all other variants, show regardless of call type
  const isAIROnlyCustomer = hasAIRSubscription && !hasCISubscription;
  const shouldEnableBanner = isAIROnlyCustomer ? isAIRCall : true;

  const bannerLocationIds = useMemo(() => (locationId ? [locationId] : []), [locationId]);

  const { handleDismiss, shouldShowBanner, isBetaBanner, ref } = useCallIntelInAppBannerVisibility({
    locationIds: bannerLocationIds,
    isDemo,
    componentId: COMPONENT_ID,
    enabled: shouldEnableBanner && !!locationId,
    bannerFF: CALL_INSIGHT_FEEDBACK_CALLOUT_BANNER_FF,
    betaBannerFF: CALL_INSIGHT_FEEDBACK_CALLOUT_BANNER_BETA_VARIANT_FF,
    showOncePerDay: true,
    maxVisibilityCount: 3,
    maxInteractionCount: 0,
    onDismiss,
  });

  // Set anchor reference for popover positioning
  useEffect(() => {
    if (anchorRef.current) {
      popoverRefs.setReference(anchorRef.current);
    }
  }, [anchorRef, popoverRefs]);

  // Open popover when banner should be shown
  useEffect(() => {
    if (shouldShowBanner) {
      open();
    }
  }, [shouldShowBanner, open]);

  const handleCalloutDismiss = () => {
    handleDismiss();
    close();
  };

  if (!shouldShowBanner) {
    return null;
  }

  const { title, description } = getVariantContent(variant);

  return (
    <PopoverDialog {...getDialogProps()} css={styles.dialog}>
      <CalloutBanner
        color={isBetaBanner ? 'indigo' : 'seaweed'}
        arrowPosition='bottom'
        badge={isBetaBanner ? 'beta' : 'new'}
        innerRef={composeRefs(ref, innerRef) as React.Ref<HTMLDivElement>}
        title={
          <Text size='medium' weight='bold'>
            {title}
          </Text>
        }
        description={<Text size='medium'>{description}</Text>}
        actions={
          <Button
            variant='tertiary'
            size='small'
            trackingId={getInAppAwarenessBannerTrackingId({
              source: COMPONENT_ID,
              isBeta: isBetaBanner,
              action: 'dismiss',
            })}
            onClick={handleCalloutDismiss}
          >
            {t('Got It')}
          </Button>
        }
      />
    </PopoverDialog>
  );
};

const styles = {
  dialog: css`
    width: 320px;
    padding: 0;
    box-shadow: none;
    overflow: hidden;
  `,
};
